import React, { useReducer, useState } from 'react'
import Modal from '../components/book/steps/components/Modal'
import {
  Input,
  SectionHeader,
  Header2,
  Text
} from '../styles/Components'
import Button from '../components/Button'
import { EditAddressModalContainer } from './account/styles'
import { gsColors } from '../styles/Variables'
import { MdLocationPin } from 'react-icons/md'
import { editAddressReducer as reducer } from './account/reducers'
import { validateAndUpdateAddress } from '../helpers/validate-and-update'
import { locateMarketForAddress } from '../services/GraphQlClientService'
import { placesResponseToAddress } from '../helpers'
import { FaLock } from 'react-icons/fa'
import { useMarketContext } from './MarketContext'
import AutoComplete from './autocomplete'


const EditAddressModal = ({
  isOpen,
  closeModal,
  isDesktop,
  width,
  user,
  setUser,
  selectedAddress,
  updateAddressForApt = null,
  lockedZipCode = false
}) => {
  const { markets } = useMarketContext()
  const [
    {
      location,
      locationError,
      pets,
      notes,
      type,
      typeError,
      apartment,
      saveError
    },
    dispatch
  ] = useReducer(reducer, {
    location: null,
    locationError: false,
    pets: selectedAddress.pets || 'None',
    notes: selectedAddress.notes || '',
    type: selectedAddress.type || '',
    typeError: false,
    apartment: selectedAddress.apartment || '',
    saveError: false
  })
  const [working, setWorking] = useState(false)
  const [showWarning, setShowWarning] = useState(false)
  const handleChange = ({ target: { name, value } }) => {
    if (name === 'type' && type === 'Hotel' && value !== 'Hotel') {
      setShowWarning(false)
    }
    dispatch({ type: 'EDIT_FORM', name, value })
  }

  const submit = () => {
    setWorking(true)
    validateAndUpdateAddress(
      { location, pets, notes, type, apartment },
      selectedAddress,
      user,
      setUser,
      closeModal,
      dispatch,
      updateAddressForApt,
      setWorking,
      markets
    )
  }

  const checkTypeAndSubmit = async () => {
    let market = markets.find(m => m.id === selectedAddress.marketId)

    // For new addresses without marketId, look up the market from the selected location
    if (!market && location) {
      const address = placesResponseToAddress(location)
      market = await locateMarketForAddress(address)
    }

    const travelModeType = market?.travelTimeCalculationOptions?.type

    return (type === 'Hotel' && travelModeType?.toLowerCase() === 'driving')
      ? setShowWarning(true)
      : submit()
  }
    


  return (
    <Modal
      isOpen={isOpen}
      closeModal={closeModal}
      width={width}
      isDesktop={isDesktop}
      bottom='40px'
      height='max-content'
    >
      {selectedAddress && (
        <EditAddressModalContainer>
          {!showWarning ? (
            <>
              <Header2 fontSize={32}>
                {selectedAddress.newOrExisting === 'new'
                  ? 'Add Address'
                  : 'Update Address'}
              </Header2>
              <div className="auto-complete">
                {lockedZipCode ? (
                  <FaLock style={{ fontSize: 16, color: gsColors.paleSlate }} />
                ) : (
                  <MdLocationPin />
                )}

                {lockedZipCode ? (
                  <Input
                    disabled
                    defaultValue={
                      selectedAddress.newOrExisting === 'existing'
                        ? `${selectedAddress.street}, ${selectedAddress.city}, ${selectedAddress.state}, USA`
                        : ''
                    }
                  />
                ) : (
                  <AutoComplete
                    defaultValue={
                      selectedAddress.newOrExisting === 'existing'
                        ? `${selectedAddress.street}, ${selectedAddress.city}, ${selectedAddress.state}, USA`
                        : ''
                    }
                    onPlaceSelected={p =>
                      dispatch({ type: 'SET_LOCATION', location: p })
                    }
                  />
                )}

                {locationError && (
                  <Text
                    variant="small"
                    color={gsColors.sunsetOrange}
                    margin=".5rem 0 0"
                  >
                    Please select an address
                  </Text>
                )}
              </div>

              <div className="location-unit">
                <Input
                  value={apartment}
                  name="apartment"
                  placeholder="Unit, Apt or Hotel room #"
                  onChange={handleChange}
                />
              </div>

              <div className="additional-info">
                <textarea
                  placeholder="Add parking information and any directions that will help us find you"
                  value={notes}
                  name="notes"
                  onChange={handleChange}
                ></textarea>
              </div>

              <div className="location-type">
                <Text weight={600}>What type of location is this?*</Text>
                <div className="location-type-buttons">
                  {['Home', 'Hotel', 'Office', 'Other'].map((t, i) => (
                    <button
                      key={i}
                      name="type"
                      value={t}
                      className={type === t ? 'selected' : ''}
                      onClick={handleChange}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                {typeError && (
                  <Text
                    variant="small"
                    color={gsColors.sunsetOrange}
                    margin=".5rem 0 0"
                  >
                    Please select a type
                  </Text>
                )}
              </div>
              {type === 'Home' && (
                <div className="pet-select">
                  <Text weight={600}>Do you have any pets?*</Text>
                  <div>
                    {['Dog', 'Cat', 'Both', 'None'].map((t, i) => (
                      <button
                        key={i}
                        name='pets'
                        value={t}
                        className={pets === t ? 'selected' : ''}
                        onClick={handleChange}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="save-address-container" style={showWarning ? { marginTop: 0 } : null}>
                <Button
                  color='#000'
                  onClick={checkTypeAndSubmit}
                  loadingColor='rgba(0, 0, 0, .3)'
                  working={working}
                  outline 
                  style={{ width: '100%' }}
                >
                  Save Address
                </Button>
                {saveError && (
                  <Text
                    variant="small"
                    color={gsColors.sunsetOrange}
                    margin=".5rem 0 0"
                  >
                    Something went wrong, try again
                  </Text>
                )}
              </div>
            </>
          ) : (
            <div className='valet-warning'>
              <SectionHeader
                margin="0 0 16px"
              >
                Our Valet Policy
              </SectionHeader>
              <Text
                textAlign='center'
                variant="small"
                margin="0 0 1rem 0"
              >
                Glamsquad Beauty Professionals will do their best to find free parking.
                If they are unable to, they reserve the right to charge you for valet parking.
                Please see our FAQs for details.
              </Text>
              <Button
                color={gsColors.sunsetOrange}
                onClick={submit}
                working={working}
              >
                {'ACCEPT & CONTINUE'}
              </Button>
            </div>
          )}
        </EditAddressModalContainer>
      )}
    </Modal>
  )
}

export default EditAddressModal
